/**
 * Heap identify lifecycle + native-pageview tracking. Inert when
 * `config.heapAppId` is unset (the transport no-ops). The `<AnalyticsProvider>`
 * component wires these into the tree.
 */

import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import type { AnyRouter } from '@tanstack/react-router'
import { useAuthContext } from '@/common/contexts/AuthContext'
import { useUserProfileContext } from '@/common/contexts/UserProfileContext'
import {
  addUserProperties,
  identify,
  reset,
  setEventBaseProps,
  setPageviewEntityProps,
  setPath,
  trackPageview,
  type PageviewEntityProps,
} from './heap'
import { buildIdentity, isUuid } from './allowlist'
import { trackSignInCompleted } from './events'
import { consumeSignInMethod } from './signInMethod'

export interface AnalyticsContextValue {
  resetIdentity: () => void
}

export const AnalyticsContext = createContext<AnalyticsContextValue | null>(null)

/**
 * Two-phase identify: (1) `identify(sub)` once the user id is available, then
 * (2) `addUserProperties` + base props + `Sign-In Completed` once the profile
 * loads. Re-runs on a segmentation-field change; resets on logout.
 */
export function useIdentifyLifecycle(): AnalyticsContextValue {
  const { isAuthenticated, user } = useAuthContext()
  const { data } = useUserProfileContext()
  const profile = data?.profile ?? null

  // Reset once on logout, never on the initial unauthenticated mount.
  const identifiedRef = useRef(false)

  const userId = isAuthenticated ? user?.user_id : undefined
  useEffect(() => {
    if (userId) {
      identify(userId)
      identifiedRef.current = true
    }
  }, [userId])

  // Deps: every field `buildIdentity` emits, so a same-session edit
  // re-propagates while a token refresh does not.
  const agentType = profile?.agent_type ?? null
  const onboardingCompleted = profile?.onboarding_completed_at ?? null
  const marketsKey = profile?.markets ? profile.markets.join('|') : ''
  const email = user?.email ?? null
  const nameKey = user ? [user.full_name, user.given_name, user.family_name].join('|') : ''
  // Re-runs the effect on the null -> loaded transition: an all-null-segmentation
  // user (pre-onboarding investor, admin) has identical derived deps before and
  // after load, so without this `identify` never fires for them.
  const profileLoaded = Boolean(profile)
  useEffect(() => {
    if (user && profile) {
      const identity = buildIdentity(user, profile)
      addUserProperties(identity)
      // Base props ride on every event + pageview; must precede the first event.
      setEventBaseProps(identity)
      // Fires only on a real sign-in: returns null on a silent session restore
      // and clears on read, so it can't double-fire.
      const method = consumeSignInMethod()
      if (method) {
        trackSignInCompleted({ method })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, agentType, onboardingCompleted, marketsKey, email, nameKey, profileLoaded])

  useEffect(() => {
    if (!isAuthenticated && identifiedRef.current) {
      reset()
      identifiedRef.current = false
    }
  }, [isAuthenticated])

  const resetIdentity = useCallback(() => {
    reset()
    identifiedRef.current = false
  }, [])

  return { resetIdentity }
}

/**
 * Extract a route template's `$id` value from the resolved pathname
 * (`/properties/$id` + `/properties/<uuid>` -> `<uuid>`). Derived from
 * pathname + template, NOT `match.params`, which lags at `onResolved`.
 */
function extractTemplateId(template: string, pathname: string): string | undefined {
  const templateSegments = template.split('/').filter(Boolean)
  const pathSegments = pathname.split('/').filter(Boolean)
  if (templateSegments.length !== pathSegments.length) {
    return undefined
  }
  for (let i = 0; i < templateSegments.length; i++) {
    if (templateSegments[i] === '$id') {
      return pathSegments[i]
    }
  }
  return undefined
}

/**
 * Resolve the entity-id pageview property for a route. Drop-unless-listed by
 * route: only `/properties/$id` and `/buybox/$id` emit an id — admin routes
 * carry another user's id and emit nothing. UUID-shape-guarded.
 */
export function resolvePageviewEntityProps(fullPath: string, pathname: string): PageviewEntityProps {
  let key: 'property_id' | 'buybox_id' | null = null
  if (fullPath.startsWith('/properties/$id')) {
    key = 'property_id'
  } else if (fullPath.startsWith('/buybox/$id')) {
    key = 'buybox_id'
  }
  if (!key) {
    return {}
  }
  const id = extractTemplateId(fullPath, pathname)
  if (!id || !isUuid(id)) {
    return {}
  }
  const props: PageviewEntityProps = {}
  props[key] = id
  return props
}

/**
 * Drive native Heap pageviews off TanStack Router. Sends the route TEMPLATE as
 * the pageview path (never the resolved URL); the entity id rides as a property.
 * Deduped by pathname so search-param churn doesn't burst pageviews. Not gated
 * on identify — pre-login navigations are captured anonymously for funnel
 * analysis. No-ops when `router` is undefined.
 */
export function usePageviewTracking(router: AnyRouter | undefined): void {
  const lastPathnameRef = useRef<string | null>(null)
  useEffect(() => {
    if (!router) {
      return
    }
    const unsubscribe = router.subscribe('onResolved', () => {
      const { matches, location } = router.state
      const leaf = matches[matches.length - 1]
      if (!leaf) {
        return
      }
      const pathname = location?.pathname ?? null
      if (pathname !== null && pathname === lastPathnameRef.current) {
        return
      }
      const isInitialNavigation = lastPathnameRef.current === null
      lastPathnameRef.current = pathname
      const entityProps = resolvePageviewEntityProps(leaf.fullPath, pathname ?? '')
      // setPath pins the unavoidable autocapture pageview to the template too.
      setPath(leaf.fullPath)
      if (isInitialNavigation) {
        // The initial autocapture pageview already covered this load; enrich it
        // with the entity id rather than firing a duplicate manual pageview.
        setPageviewEntityProps(entityProps)
      } else {
        trackPageview(leaf.fullPath, leaf.routeId, entityProps)
      }
    })
    return unsubscribe
  }, [router])
}

/** Access the analytics context (e.g. to call `resetIdentity` on logout). */
export function useAnalyticsContext(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider')
  }
  return context
}
